import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import APIDefinitionService from "../Services/APIDefinition";
import ProjectService from "../Services/ProjectService";
import ApiBuilderForm from "./ApiBuilderForm";
import ProjectDocuments from "./ProjectDocuments";
import "../CSS/ProjectView.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UserToModuleService from "../Services/UsertoModuleService";
import { useAuth } from "../Services/AuthContext";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

// import ReactJson from "react-json-view";

const ProjectView = ({ toggleSidebar, setRefreshProjects }) => {
  const { organizationId, projectId } = useParams();
  const navigate = useNavigate();
  const [apis, setApis] = useState([]);
  const [project, setProject] = useState(null);
  const [showApiForm, setShowApiForm] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedApis, setExpandedApis] = useState({});

  const [activeMainTab, setActiveMainTab] = useState(() => {
    return localStorage.getItem("project-active-tab") || "apis";
  });

  const [allExpanded, setAllExpanded] = useState(false);

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testingApi, setTestingApi] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState("local");
  const testSectionRef = React.useRef(null);
  const [testMode, setTestMode] = useState("browser"); // "browser" | "server"

  const [selectedMethod, setSelectedMethod] = useState("ALL");

  const [searchTerm, setSearchTerm] = useState("");

  const [allowedModuleIds, setAllowedModuleIds] = useState(null);
  const { user, loading: authLoading } = useAuth();
  const userId = user?.userId || null;

  // Postman UI states
  const [activeTab, setActiveTab] = useState("Params");
  const [editableUrl, setEditableUrl] = useState("");

  const [paramValues, setParamValues] = useState({});
  const [headers, setHeaders] = useState([
    { key: "Content-Type", value: "application/json" },
  ]);

  const [requestBody, setRequestBody] = useState("{}");
  const [copiedApiId, setCopiedApiId] = useState(null);

  const [documentsExpanded, setDocumentsExpanded] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [apiToDelete, setApiToDelete] = useState(null);

  const prevCountsRef = React.useRef({});

  const getBasePath = () => {
    switch (selectedEnv) {
      case "local":
        return project?.localBasePath;
      case "server":
        return project?.serverBasePath;
      case "stage":
        return project?.stageBasePath;
      default:
        return "";
    }
  };
  const CountUpBadge = ({ moduleId, count, apis }) => {
    const [displayCount, setDisplayCount] = React.useState(0);
    const [effect, setEffect] = React.useState("");

    const prevCount = prevCountsRef.current[moduleId] ?? 0;

    // 🔹 Find dominant HTTP method
    const dominantMethod = React.useMemo(() => {
      if (!apis || apis.length === 0) return "";

      const freq = {};
      apis.forEach((a) => {
        const m = a.method?.toUpperCase();
        if (m) freq[m] = (freq[m] || 0) + 1;
      });

      return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0];
    }, [apis]);

    React.useEffect(() => {
      if (count > prevCount) {
        setEffect(`glow ${dominantMethod}`);
      } else if (count < prevCount) {
        setEffect("shake");
      }

      let current = prevCount;
      const step = Math.max(1, Math.ceil((count - prevCount) / 10));

      const interval = setInterval(() => {
        current += step;
        if ((step > 0 && current >= count) || (step < 0 && current <= count)) {
          current = count;
          clearInterval(interval);
        }
        setDisplayCount(current);
      }, 30);

      prevCountsRef.current[moduleId] = count;

      const timeout = setTimeout(() => setEffect(""), 600);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }, [count, moduleId, prevCount, dominantMethod]);

    return (
      <span className={`module-badge ${effect}`}>
        {displayCount} API{displayCount !== 1 ? "s" : ""}
      </span>
    );
  };

  useEffect(() => {
    if (projectId) {
      reloadProjectData();
    }
  }, [projectId]);

  useEffect(() => {
    if (!userId) return;

    const fetchUserModules = async () => {
      try {
        const res = await UserToModuleService.getUserToModuleIdByUserId(userId);

        console.log("Raw response:", res.data);

        //STEP 1: flatten modules from response
        const modulesFromApi = res.data.flatMap((item) => item.modules || []);

        //STEP 2: extract module IDs
        const moduleIds = modulesFromApi.map((m) => Number(m.id));

        console.log("Allowed Module IDs:", moduleIds);

        setAllowedModuleIds(moduleIds);
      } catch (error) {
        console.error("Failed to load user modules", error);
      }
    };

    fetchUserModules();
  }, [userId]);

  useEffect(() => {
    localStorage.setItem("project-active-tab", activeMainTab);
  }, [activeMainTab]);

  // 🔹 Restore env when project loads
  useEffect(() => {
    if (projectId) {
      const savedEnv = localStorage.getItem(`project-env-${projectId}`);

      if (savedEnv) {
        setSelectedEnv(savedEnv);
      }
    }
  }, [projectId]);

  // 🔹 Persist env when user changes it
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(`project-env-${projectId}`, selectedEnv);
    }
  }, [selectedEnv, projectId]);

  // 🔹 Auto-switch Test Mode based on selected Base Path
  useEffect(() => {
    if (selectedEnv === "local") {
      setTestMode("browser");
    } else if (selectedEnv === "server" || selectedEnv === "stage") {
      setTestMode("server");
    }
  }, [selectedEnv]);

  const toggleExpandCollapseAll = () => {
    if (activeMainTab === "apis") {
      if (!allExpanded) {
        const moduleState = {};
        const apiState = {};

        project?.modules?.forEach((module) => {
          moduleState[module.id] = true;
          module.apiDefinitions?.forEach((api) => {
            apiState[api.id] = true;
          });
        });

        setExpandedModules(moduleState);
        setExpandedApis(apiState);
      } else {
        setExpandedModules({});
        setExpandedApis({});
      }

      setAllExpanded(!allExpanded);
    }

    if (activeMainTab === "documents") {
      setDocumentsExpanded((prev) => !prev);
    }
  };

  const reloadProjectData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [projectRes, apisRes] = await Promise.all([
        ProjectService.getProjectById(organizationId, projectId),

        APIDefinitionService.getApisByProject(projectId),
      ]);
      setProject(projectRes.data);
      setApis(apisRes.data);
    } catch (error) {
      console.error("Error loading project or APIs:", error);
      setError("Failed to load project data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditApi = (api) => {
    setEditingApi(api);
    setShowApiForm(true);
  };

  const handleDeleteApi = async () => {
    if (!apiToDelete) return;

    try {
      await APIDefinitionService.deleteApiDefinition(apiToDelete.id);
      reloadProjectData();
    } catch (error) {
      console.error("Error deleting API:", error);
      setError("Failed to delete API definition");
    } finally {
      setConfirmDeleteOpen(false);
      setApiToDelete(null);
    }
  };

  const handleViewDocumentation = () => {
    window.open(
      `/organization/${organizationId}/project/${projectId}/yaml`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleDeleteProject = async () => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await ProjectService.deleteProject(organizationId, projectId);
        setRefreshProjects((prev) => !prev);
        navigate("/");
      } catch (error) {
        setError("Failed to delete project");
      }
    }
  };

  useEffect(() => {
    if (!testingApi) return;

    const hasBody = ["POST", "PUT", "PATCH"].includes(
      testingApi.method?.toUpperCase(),
    );

    if (!hasBody) {
      setRequestBody("");
      return;
    }

    // 🔥 1️⃣ RAW MODE (Postman style)
    if (testingApi.bodyInputMode === "RAW" && testingApi.rawJson) {
      setRequestBody(testingApi.rawJson);
      return;
    }

    // 🔥 2️⃣ FORM MODE (fields-based)
    if (testingApi.fields?.length) {
      const body = {};

      testingApi.fields.forEach((field) => {
        let value = field.example;

        if (typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch {}
        }

        body[field.name] =
          value !== undefined ? value : field.type === "number" ? 0 : "";
      });

      setRequestBody(JSON.stringify(body, null, 2));
      return;
    }

    // 🔥 fallback
    setRequestBody("{}");
  }, [testingApi]);

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const toggleApiDetails = (apiId) => {
    setExpandedApis((prev) => ({
      ...prev,
      [apiId]: !prev[apiId],
    }));
  };

  const copyToClipboard = async (text, apiId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedApiId(apiId);

      setTimeout(() => {
        setCopiedApiId(null);
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const handleApiSubmit = async (apiData) => {
    try {
      const apiDefinition = {
        ...apiData,
        project: { id: projectId },
      };

      if (editingApi) {
        await APIDefinitionService.updateApiDefinition(
          editingApi.id,
          apiDefinition,
        );
      } else {
        await APIDefinitionService.createApiDefinition(apiDefinition);
      }

      setError(null);

      // ✅ SAFE CHECK
      if (typeof setRefreshProjects === "function") {
        // setRefreshProjects((prev) => !prev);
      }

      setShowApiForm(false);
      setEditingApi(null);
      reloadProjectData();
    } catch (error) {
      console.error("Error saving API:", error);
      setError("Failed to save API definition");
      throw error; // VERY IMPORTANT
    }
  };

  // const runApiTest = async () => {
  //     if (!testingApi) return;

  //     setIsTesting(true);
  //     setTestResult(null);

  //     try {
  //         const res = await axios.get(
  //             `http://localhost:2011/api/tester/test/${testingApi.id}?useLocal=${selectedEnv === "local"}`
  //         );
  //         setTestResult(res.data);
  //     } catch (err) {
  //         setTestResult({
  //             status: 500,
  //             body: err.message,
  //             headers: {},
  //             timeTakenMs: 0
  //         });
  //     }

  //     setIsTesting(false);
  // };

  const runApiTest = async () => {
    if (!testingApi) return;

    // 🔥 ALWAYS switch to Test Result tab
    setActiveTab("Test Result");

    setIsTesting(true);
    setTestResult(null);

    try {
      if (testMode === "browser") {
        await runBrowserApiTest();
      } else {
        const res = await axios.post(
          `http://localhost:2011/api/tester/test/${testingApi.id}`,
          null,
          {
            params: { environment: selectedEnv.toUpperCase() },
          },
        );

        setTestResult(res.data);
      }
    } catch (err) {
      if (err.response) {
        setTestResult(err.response.data);
      } else {
        setTestResult({
          status: 500,
          body: err.message,
          headers: {},
          timeTakenMs: 0,
        });
      }
    }

    setIsTesting(false);
  };

  const openTestModal = (api) => {
    setTestingApi(api);

    const fullUrl = (getBasePath() || "") + api.fullPath;
    setEditableUrl(fullUrl);

    // 🔥 Auto-fill saved param examples/defaults
    if (api.params?.length) {
      const initialParams = {};
      api.params.forEach((p) => {
        initialParams[p.name] = p.example || p.defaultValue || "";
      });
      setParamValues(initialParams);
    }

    setTestModalOpen(true);

    // Instantly scroll without delay
    requestAnimationFrame(() => {
      if (testSectionRef.current) {
        // 🔥 Auto-fill saved param examples/defaults
        if (api.params?.length) {
          const initialParams = {};
          api.params.forEach((p) => {
            initialParams[p.name] = p.example || p.defaultValue || "";
          });
          setParamValues(initialParams);
        }

        testSectionRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }
    });
  };
  const buildFinalPath = (path, params, paramValues) => {
    let finalPath = path;

    params
      ?.filter((p) => p.paramIn === "path")
      .forEach((p) => {
        const value = paramValues[p.name];

        if (!value) {
          throw new Error(`Missing path parameter: ${p.name}`);
        }

        finalPath = finalPath.replace(`{${p.name}}`, encodeURIComponent(value));
      });

    return finalPath;
  };

  const runBrowserApiTest = async () => {
    if (!testingApi) return;

    const hasBody =
      ["POST", "PUT", "PATCH"].includes(testingApi?.method?.toUpperCase()) &&
      (testingApi?.bodyInputMode === "RAW" ||
        (testingApi?.fields && testingApi.fields.length > 0));

    setIsTesting(true);
    setTestResult(null);

    try {
      const baseUrl = getBasePath();

      if (!baseUrl) {
        throw new Error(`Base path not configured for ${selectedEnv}`);
      }

      // Build query params
      const queryString = Object.entries(paramValues)
        .filter(([_, v]) => v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");

      const resolvedPath = buildFinalPath(
        testingApi?.fullPath,
        testingApi?.params,
        paramValues,
      );

      const finalUrl =
        editableUrl ||
        baseUrl + resolvedPath + (queryString ? `?${queryString}` : "");

      // ✅ FIX-2: Validate JSON before sending
      if (hasBody && requestBody?.trim()) {
        try {
          JSON.parse(requestBody);
        } catch {
          throw new Error("Request body is not valid JSON");
        }
      }

      let fetchOptions = {
        method: testingApi?.method,
        headers: {
          ...headers.reduce((acc, h) => {
            acc[h.key] = h.value;
            return acc;
          }, {}),
        },
      };

      // 🔥 HANDLE FORM DATA
      if (testingApi?.bodyInputMode === "FORM_DATA") {
        const formData = new FormData();

        testingApi.formFields?.forEach((field) => {
          if (!field.key) return;

          if (field.fieldType === "file") {
            // ⚠ Browser cannot use stored filePath
            // You must let user re-upload file in test modal
            // So you need a new state like uploadedTestFiles

            const fileInput = document.querySelector(
              `input[name="test-file-${field.key}"]`,
            );

            if (fileInput?.files[0]) {
              formData.append(field.key, fileInput.files[0]);
            }
          } else {
            formData.append(field.key, field.value || "");
          }
        });

        fetchOptions.body = formData;

        // 🚨 VERY IMPORTANT
        delete fetchOptions.headers["Content-Type"];
      }

      // 🔥 HANDLE RAW JSON
      else if (testingApi?.bodyInputMode === "RAW") {
        fetchOptions.headers["Content-Type"] = "application/json";
        fetchOptions.body = requestBody;
      }

      const response = await fetch(finalUrl, fetchOptions);

      const text = await response.text();

      setTestResult({
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
        body: tryParseJson(text),
        timeTakenMs: 0,
      });
    } catch (err) {
      setTestResult({
        status: 500,
        body: err.message,
        headers: {},
        timeTakenMs: 0,
      });
    }

    setIsTesting(false);
  };

  const tryParseJson = (text) => {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  };

  const closeTestModal = () => {
    setTestModalOpen(false);
    setTestingApi(null);
    setTestResult(null);
  };

  if (error) {
    return (
      <div
        className={`project-view-container ${
          activeMainTab === "documents" ? "documents-fullwidth" : ""
        }`}
      >
        <button className="hamburger" onClick={toggleSidebar}>
          ☰
        </button>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-view-container">
        <button className="hamburger" onClick={toggleSidebar}>
          ☰
        </button>
        <div>Project not found</div>
      </div>
    );
  }

  const canDeleteProject =
    project &&
    project.modules &&
    project.modules.every((m) => m.apiDefinitions.length === 0);

  // Query Params Change Handler
  const handleParamChange = (name, value) => {
    setParamValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Header Change Handler
  const handleHeaderChange = (index, value) => {
    const updated = [...headers];
    updated[index].value = value;
    setHeaders(updated);
  };

  const filteredModules = project?.modules
    ?.filter(
      (module) =>
        allowedModuleIds === null ||
        allowedModuleIds.includes(Number(module.id)),
    )

    .map((module) => {
      const filteredApis = module.apiDefinitions.filter((api) => {
        const matchesSearch =
          api.fullPath?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          api.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          api.method?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          api.schemaName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesMethod =
          selectedMethod === "ALL" ||
          api.method?.toUpperCase() === selectedMethod;

        return matchesSearch && matchesMethod;
      });

      return { ...module, apiDefinitions: filteredApis };
    });

  const methodCounts =
    project?.modules?.reduce((acc, module) => {
      module.apiDefinitions?.forEach((api) => {
        const method = api.method?.toUpperCase();
        acc[method] = (acc[method] || 0) + 1;
      });
      return acc;
    }, {}) || {};

  const totalApiCount =
    project?.modules?.reduce(
      (count, module) => count + (module.apiDefinitions?.length || 0),
      0,
    ) || 0;

  // Expand all modules and APIs
  const expandAll = () => {
    const moduleState = {};
    const apiState = {};

    project?.modules?.forEach((module) => {
      moduleState[module.id] = true;

      module.apiDefinitions?.forEach((api) => {
        apiState[api.id] = true;
      });
    });

    setExpandedModules(moduleState);
    setExpandedApis(apiState);
  };

  // Collapse all modules and APIs
  const collapseAll = () => {
    setExpandedModules({});
    setExpandedApis({});
  };

  const handleTabClick = (tab, e) => {
    const button = e.currentTarget;

    // remove previous ripple
    button.classList.remove("ripple");

    // force reflow (restart animation)
    void button.offsetWidth;

    // add ripple
    button.classList.add("ripple");

    // change tab
    setActiveMainTab(tab);
  };

  return (
    <div className="project-view-container">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />

      <div className="project-header">
        <div className="project-title-row">
          <h2>{project.name}</h2>

          {activeMainTab === "apis" && (
            <input
              type="text"
              className="project-api-search"
              placeholder="Search APIs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
        </div>
      </div>

      <p className="project-description">{project.description}</p>

      {/* ================= ENV + FILTER BAR ================= */}

      {activeMainTab === "apis" && (
        <div className="env-selector-container">
          {/* ENV SWITCH */}
          <span className="env-label">Base Path:</span>
          <select
            className="env-dropdown"
            value={selectedEnv}
            onChange={(e) => setSelectedEnv(e.target.value)}
          >
            <option value="local">Local</option>
            <option value="server">Production</option>
            <option value="stage">Stage</option>
          </select>

          <span className="env-basepath-display">
            {getBasePath() || "Not configured"}
          </span>

          {/* TEST MODE */}
          <div className="method-filter">
            <label className="env-label">Test Mode:</label>
            <select
              className="env-dropdown"
              value={testMode}
              onChange={(e) => setTestMode(e.target.value)}
            >
              <option value="browser">Run in Browser</option>
              <option value="server">Run on Server</option>
            </select>
          </div>

          {/* METHOD FILTER */}
          <div className="method-filter">
            <label className="env-label">Method:</label>
            <select
              className="env-dropdown"
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
            >
              <option value="ALL">All ({totalApiCount})</option>

              {Object.entries(methodCounts).map(([method, count]) => (
                <option key={method} value={method}>
                  {method} ({count})
                </option>
              ))}
            </select>
          </div>

          {/* TOTAL COUNT */}
          <span className="api-count-display">
            Total APIs: <strong>{totalApiCount}</strong>
          </span>
        </div>
      )}

      {/* ================= MAIN TABS ================= */}
      <div className="project-main-tabs">
        <button
          className={activeMainTab === "apis" ? "tab active" : "tab"}
          data-ripple="blue"
          onClick={(e) => handleTabClick("apis", e)}
        >
          APIs
        </button>

        <button
          className={activeMainTab === "documents" ? "tab active" : "tab"}
          data-ripple="green"
          onClick={(e) => handleTabClick("documents", e)}
        >
          Documents
        </button>
      </div>

      {activeMainTab === "apis" && (
        <div className="project-actions">
          <button
            className="btn create-btn"
            onClick={() => {
              setError(null);
              setShowApiForm(true);
            }}
          >
            Create New API
          </button>

          <button
            className="btn documentation-btn"
            onClick={handleViewDocumentation}
          >
            View Documentation
          </button>
          <button
            className="btn update-btn"
            onClick={() =>
              navigate(
                `/organization/${organizationId}/project/${projectId}/edit`,
              )
            }
          >
            Update Project
          </button>

          <button
            className="btn delete-btn"
            onClick={handleDeleteProject}
            disabled={!canDeleteProject}
            title={
              !canDeleteProject
                ? "Cannot delete a project that contains APIs"
                : ""
            }
            style={{
              opacity: !canDeleteProject ? 0.5 : 1,
              cursor: !canDeleteProject ? "not-allowed" : "pointer",
            }}
          >
            Delete Project
          </button>
        </div>
      )}

      {showApiForm && (
        <div className="api-form-modal">
          <div className="api-form-content">
            <button
              className="close-button"
              onClick={() => {
                setShowApiForm(false);
                setEditingApi(null);
              }}
            >
              ×
            </button>

            <ApiBuilderForm
              initialData={editingApi}
              onSubmit={handleApiSubmit}
              projectId={projectId}
              onCancel={() => {
                setShowApiForm(false);
                setEditingApi(null);
              }}
            />
          </div>
        </div>
      )}

      {/* ================= MAIN TAB CONTENT ================= */}
      <div className="tab-content">
        {/* ================= APIs TAB ================= */}
        {activeMainTab === "apis" && (
          <div className="tab-pane active">
            <div className="api-list-container">
              <h3>APIs in this Project</h3>

              {!project.modules || project.modules.length === 0 ? (
                <p className="no-apis-message">
                  No modules found for this project.
                </p>
              ) : (
                filteredModules.map((module) => (
                  <div key={module.id} className="api-module">
                    {/* MODULE HEADER */}
                    <div
                      className="module-header"
                      onClick={() => toggleModule(module.id)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span className="module-name">{module.name}</span>

                      {/* RIGHT SIDE: API Count + Arrow */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <CountUpBadge
                          moduleId={module.id}
                          count={module.apiDefinitions.length}
                          apis={module.apiDefinitions}
                        />

                        <span className="module-arrow">
                          {expandedModules[module.id] ? "▼" : "▶"}
                        </span>
                      </div>
                    </div>

                    {/* MODULE BODY */}
                    <div
                      className={`module-body ${
                        expandedModules[module.id] ? "open" : ""
                      }`}
                    >
                      {module.apiDefinitions.map((api) => {
                        const isOpen = expandedApis[api.id];

                        return (
                          <div key={api.id} className="swagger-api-row">
                            {/* API SUMMARY */}
                            <div
                              className={`api-summary ${api.method}`}
                              onClick={() => toggleApiDetails(api.id)}
                            >
                              <span className={`method-badge ${api.method}`}>
                                {api.method}
                              </span>

                              <span className="path">
                                {getBasePath() || ""}
                                {api.fullPath}
                              </span>

                              <div className="copy-wrapper">
                                <button
                                  className="copy-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(
                                      (getBasePath() || "") + api.fullPath,
                                      api.id,
                                    );
                                  }}
                                >
                                  📋
                                </button>

                                {copiedApiId === api.id && (
                                  <span className="copy-tooltip">Copied!</span>
                                )}
                              </div>

                              <span className="arrow">
                                {isOpen ? "▼" : "▶"}
                              </span>
                            </div>

                            {/* API DETAILS */}
                            <div
                              className={`api-details ${isOpen ? "open" : ""}`}
                            >
                              <div className="api-action-buttons">
                                <button
                                  className="edit-btn"
                                  onClick={() => handleEditApi(api)}
                                >
                                  Edit
                                </button>

                                <button
                                  className="delete-btn"
                                  onClick={() => {
                                    setApiToDelete(api);
                                    setConfirmDeleteOpen(true);
                                  }}
                                >
                                  Delete
                                </button>

                                <button
                                  className="test-btn"
                                  onClick={() => openTestModal(api)}
                                >
                                  ▶ Test
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ================= DOCUMENTS TAB ================= */}
        {activeMainTab === "documents" && (
          <div className="tab-pane active">
            <ProjectDocuments expandAll={documentsExpanded} />
          </div>
        )}
      </div>

      <Dialog
        open={testModalOpen}
        onClose={closeTestModal}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        sx={{
          backdropFilter: "blur(6px)",
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.6)",
          },
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            backgroundColor: "#1e1e1e",
            color: "#fff",
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Testing API: {testingApi?.fullPath}
          <IconButton onClick={closeTestModal} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            padding: 0,
            backgroundColor: "#1e1e1e",
          }}
        >
          {!testingApi ? null : (
            <div className="test-api-container">
              <div className="pm-container">
                {/* ---------------- HEADER ---------------- */}
                <div className="pm-header">
                  <div
                    className={`pm-method ${testingApi?.method?.toLowerCase() || ""}`}
                  >
                    {testingApi?.method}
                  </div>

                  <input
                    className="pm-url-input"
                    value={editableUrl}
                    onChange={(e) => setEditableUrl(e.target.value)}
                  />

                  <button
                    className="pm-send-btn"
                    onClick={runApiTest}
                    disabled={isTesting}
                  >
                    {isTesting ? "Sending..." : "Send"}
                  </button>
                </div>

                {/* ---------------- TABS ---------------- */}
                <div className="pm-tabs">
                  {["Params", "Headers", "Body", "Test Result"].map((tab) => (
                    <div
                      key={tab}
                      className={`pm-tab ${activeTab === tab ? "active" : ""}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab}
                    </div>
                  ))}
                </div>

                {/* ================= TAB CONTENT ================= */}

                {activeTab === "Params" && (
                  <div className="pm-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Key</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testingApi?.params?.map((param, index) => (
                          <tr key={index}>
                            <td>
                              {param.name}

                              <span style={{ color: "#aaa", marginLeft: 6 }}>
                                ({param.paramIn})
                              </span>
                              {param.required && (
                                <span style={{ color: "red" }}> *</span>
                              )}
                            </td>
                            <td>
                              <input
                                className="pm-input"
                                value={paramValues[param.name] || ""}
                                onChange={(e) =>
                                  handleParamChange(param.name, e.target.value)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeTab === "Headers" && (
                  <div className="pm-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Header</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {headers.map((h, index) => (
                          <tr key={index}>
                            <td>{h.key}</td>
                            <td>
                              <input
                                className="pm-input"
                                value={h.value}
                                onChange={(e) =>
                                  handleHeaderChange(index, e.target.value)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeTab === "Body" &&
                  (testingApi?.bodyInputMode === "FORM_DATA" ? (
                    <div className="pm-formdata-container">
                      {testingApi?.formFields?.map((field, index) => (
                        <div key={index} style={{ marginBottom: "12px" }}>
                          <label
                            style={{ display: "block", marginBottom: "4px" }}
                          >
                            {field.key}
                          </label>

                          {field.fieldType === "file" ? (
                            <input
                              type="file"
                              name={`test-file-${field.key}`}
                            />
                          ) : (
                            <input
                              type="text"
                              defaultValue={field.value || ""}
                              className="pm-input"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pm-json-editor">
                      <textarea
                        className="pm-json-textarea"
                        value={requestBody}
                        onChange={(e) => setRequestBody(e.target.value)}
                      />
                    </div>
                  ))}

                {activeTab === "Test Result" && (
                  <div className="pm-response">
                    {!testResult ? (
                      <div style={{ padding: "20px", color: "#aaa" }}>
                        No request sent yet.
                      </div>
                    ) : (
                      <>
                        {/* 🔹 STATUS + TIME */}
                        <div style={{ marginBottom: "15px" }}>
                          <strong>Status:</strong> {testResult.status} <br />
                          <strong>Time:</strong> {testResult.timeTakenMs} ms
                        </div>

                        {/* 🔹 RESPONSE HEADERS */}
                        {testResult.headers && (
                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ marginBottom: "5px" }}>
                              Response Headers
                            </h4>
                            <pre
                              style={{
                                background: "#111",
                                padding: "10px",
                                borderRadius: "6px",
                                overflowX: "auto",
                              }}
                            >
                              {JSON.stringify(testResult.headers, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* 🔹 RESPONSE BODY */}
                        <h4 style={{ marginBottom: "5px" }}>Response Body</h4>
                        <pre
                          style={{
                            background: "#111",
                            padding: "15px",
                            borderRadius: "6px",
                            overflowX: "auto",
                            color: "#fff",
                            maxHeight: "500px",
                            overflowY: "auto",
                          }}
                        >
                          {JSON.stringify(
                            typeof testResult?.body === "string"
                              ? tryParseJson(testResult.body)
                              : testResult?.body || {},
                            null,
                            2,
                          )}
                        </pre>
                      </>
                    )}
                  </div>
                )}

                <div className="pm-status-bar">
                  {testResult ? (
                    <>
                      <span
                        className={`pm-status-code ${
                          testResult.status >= 200 && testResult.status < 300
                            ? "ok"
                            : "err"
                        }`}
                      >
                        {testResult.status}
                      </span>
                      <span className="pm-time">
                        {testResult.timeTakenMs} ms
                      </span>
                    </>
                  ) : (
                    <span className="pm-empty-status">No request sent yet</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {confirmDeleteOpen && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>Delete API</h3>

            <p>
              Are you sure you want to delete
              <strong> {apiToDelete?.fullPath}</strong>?
            </p>

            <div className="confirm-actions">
              <button
                className="btn cancel-btn"
                onClick={() => {
                  setConfirmDeleteOpen(false);
                  setApiToDelete(null);
                }}
              >
                Cancel
              </button>

              <button className="btn delete-btn" onClick={handleDeleteApi}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectView;
